export type ShopFilters = {
  marketName: string|null;
  priceUsd: number|null;
  onlyAvailable: boolean;
  extraFilters: ShopExtraFilters|null;
};

export type ShopExtraFilters = ShopCsgoExtraFilters |
                              ShopDota2ExtraFilters |
                              ShopRustExtraFilters;

export type ShopCsgoExtraFilters = {
  type: CSGO_ItemType|null;
};

export type ShopDota2ExtraFilters = {
  type: Dota2ItemType|null;
};

export type ShopRustExtraFilters = {
  type: RustItemType|null;
};

export type CSGO_Extras = {
  n: string[]; // nameParts
  t: CSGO_ItemType; // type
  g: number; // gunType, CSGO_GunType
  e: CSGO_Exterior; // exterior
  r: CSGO_Rarity; // rarity
  st: boolean; // isStattrak
};

export const enum CSGO_Rarity {
  BASE_GRADE = 1,
  DISTINGUISHED = 2,
  EXCEPTIONAL = 3,
  MASTER = 4,
  SUPERIOR = 5,
  EXOTIC = 6,
  EXTRAORDINARY = 7,
  HIGH_GRADE = 8,
  REMARKABLE = 9,
  COVERT = 10,
  CONTRABAND = 11,
  CLASSIFIED = 12,
  CONSUMER = 13,
  INDUSTRIAL = 14,
  MIL_SPEC = 15,
  RESTRICTED = 16
}

export const enum CSGO_ItemType {
  ACCESS_PASS = 1,
  AGENT = 2,
  COLLECTIBLE = 3,
  CONTAINER = 4,
  GIFT = 5,
  GLOVES = 6,
  GRAFFITI = 7,
  KEY = 8,
  KNIFE = 9,
  MUSIC_KIT = 10,
  PASS = 11,
  PATCH = 12,
  STICKER = 13,
  TAG = 14,
  TOOL = 15,
  WEAPON = 16
}

export const enum CSGO_Exterior {
  BS = 1,
  FN = 2,
  FT = 3,
  MW = 4,
  WW = 5,
  NP = 6
}

export const CSGO_STATTRAK_COLOR = 'cf6a32';

export const CSGO_COLORS: Record<CSGO_Rarity, string> = {
  [CSGO_Rarity.BASE_GRADE]: 'b0c3d9',
  [CSGO_Rarity.DISTINGUISHED]: '4b69ff',
  [CSGO_Rarity.EXCEPTIONAL]: '8847ff',
  [CSGO_Rarity.MASTER]: 'eb4b4b',
  [CSGO_Rarity.SUPERIOR]: 'd32ce6',
  [CSGO_Rarity.EXOTIC]: 'd32ce6',
  [CSGO_Rarity.EXTRAORDINARY]: 'eb4b4b',
  [CSGO_Rarity.HIGH_GRADE]: '4b69ff',
  [CSGO_Rarity.REMARKABLE]: '8847ff',
  [CSGO_Rarity.COVERT]: 'eb4b4b',
  [CSGO_Rarity.CONTRABAND]: 'e4ae39',
  [CSGO_Rarity.CLASSIFIED]: 'd32ce6',
  [CSGO_Rarity.CONSUMER]: 'b0c3d9',
  [CSGO_Rarity.INDUSTRIAL]: '5e98d9',
  [CSGO_Rarity.MIL_SPEC]: '4b69ff',
  [CSGO_Rarity.RESTRICTED]: '8847ff'
};

export const CSGO_EXTERIORS: Record<CSGO_Exterior, string> = {
  [CSGO_Exterior.BS]: 'BS',
  [CSGO_Exterior.FN]: 'FN',
  [CSGO_Exterior.FT]: 'FT',
  [CSGO_Exterior.MW]: 'MW',
  [CSGO_Exterior.WW]: 'WW',
  [CSGO_Exterior.NP]: 'NP'
};

export type Item = {
  id: number;
  marketName: string;
  stock: number;
  priceUsd: number;
  appId: SteamApp;
  extra: CSGO_Extras |
         Dota2_Extras |
         Rust_Extras |
         null;
};

export enum SteamApp {
  DOTA2 = 570,
  RUST = 252490,
  CSGO = 730
}

export type AvailableApp = {
  title: string;
  icon: string;
  appId: SteamApp;
};

export const AVAILABLE_APPS_MAP: Record<SteamApp, AvailableApp> = {
  [SteamApp.CSGO]: { title: 'CS2', icon: 'assets/images/csgo-icon.svg', appId: SteamApp.CSGO },
  [SteamApp.DOTA2]: { title: 'Dota 2', icon: 'assets/images/dota2-icon.svg', appId: SteamApp.DOTA2 },
  [SteamApp.RUST]: { title: 'Rust', icon: 'assets/images/rust-icon.svg', appId: SteamApp.RUST }
};

export const AVAILABLE_APPS = [
  { title: 'CS2', icon: 'assets/images/csgo-icon.svg', appId: SteamApp.CSGO },
  { title: 'Dota 2', icon: 'assets/images/dota2-icon.svg', appId: SteamApp.DOTA2 },
  { title: 'Rust', icon: 'assets/images/rust-icon.svg', appId: SteamApp.RUST }
];

export type Dota2_Extras = {
  r: Dota2ItemRarity|null;
  q: number|null; // Dota2ItemQuality
  t: Dota2ItemType|null;
  h: number|null; // Dota2ItemHero
};

export enum Dota2ItemRarity {
  COMMON = 1,
  UNCOMMON = 2,
  RARE = 3,
  MYTHICAL = 4,
  LEGENDARY = 5,
  IMMORTAL = 6,
  ANCIENT = 7,
  ARCANA = 8,
  SEASONAL = 9
}

export const enum Dota2ItemType {
  COURIER = 1,
  WEARABLE = 2,
  GEM_RUNE = 3,
  WARD = 4,
  EMBLEM = 5,
  LOADING_SCREEN = 6,
  BUNDLE = 7,
  LEAGUE = 8,
  MUSIC = 9,
  PLAYER_CARD = 10,
  HUD_SKIN = 11,
  TREASURE = 12,
  ANNOUNCER = 13,
  TOOL = 14,
  RETIRED_CHEST = 15,
  EMOTICON_TOOL = 16,
  MISC = 17,
  PENNANT = 18,
  TAUNT = 19,
  TREASURE_KEY = 20,
  CURSOR_PACK = 21,
  TERRAIN = 22,
  STICKER = 23,
  STICKER_CAPSULE = 24
}

export const DOTA2_COLORS: Record<Dota2ItemRarity, string> = {
  [Dota2ItemRarity.COMMON]: 'b0c3d9',
  [Dota2ItemRarity.UNCOMMON]: '5e98d9',
  [Dota2ItemRarity.RARE]: '4b69ff',
  [Dota2ItemRarity.MYTHICAL]: '8847ff',
  [Dota2ItemRarity.LEGENDARY]: 'd32ce6',
  [Dota2ItemRarity.IMMORTAL]: 'e4ae39',
  [Dota2ItemRarity.ANCIENT]: 'eb4b4b',
  [Dota2ItemRarity.ARCANA]: 'a7ec2e', // TODO add green color
  [Dota2ItemRarity.SEASONAL]: 'b0c3d9'
};

export type Rust_Extras = {
  c: RustItemCategory|null;
  t: RustItemType|null;
  r: null;
};

export enum RustItemCategory {
  WEAPON = 1,
  CLOTHING = 2,
  ARMOR = 3,
  MISC = 4,
  RESOURCE = 5
}

export const enum RustItemType {
  DOUBLE_BARREL_SHOTGUN = 1,
  STONE_HATCHET = 2,
  ROADSIGN_JACKET = 3,
  SEMI_AUTO_RIFLE = 4,
  ROCKET_LAUNCHER = 5,
  METAL_FACEMASK = 6,
  HOODIE = 7,
  AK47U = 8,
  RIFLE_HELMET = 9,
  SHEET_METAL_DOOR = 10,
  BOLT_RIFLE = 11,
  BOOTS = 12,
  REVOLVER = 13,
  ROADSIGN_KILT = 14,
  TSHIRT = 15,
  LARGE_WOODEN_BOX = 16,
  PUMP_SHOTGUN = 17,
  MP5 = 18,
  PANTS = 19,
  HATCHET = 20,
  BURLAP_TROUSERS = 21,
  GUITAR = 22,
  LONGSWORD = 23,
  BURLAP_GLOVES = 24,
  SHORTS = 25,
  ARMORED_METAL_DOOR = 26,
  HAMMER = 27,
  METAL_TORSO_PLATE = 28,
  SALVAGED_SWORD = 29,
  SEMI_AUTO_PISTOL = 30,
  WOODEN_BOX = 31,
  BONE_CLUB = 32,
  SMG = 33,
  SLEEPING_BAG = 34,
  WOODEN_DOOR = 35,
  LONG_TSHIRT = 36,
  THOMPSON = 37,
  BALACLAVA = 38,
  SNOW_JACKET = 39,
  HIDE_PANTS = 40,
  BONE_KNIFE = 41,
  BURLAP_SHIRT = 42,
  STONE_PICKAXE = 43,
  ROCK = 44,
  WATERPIPE_SHOTGUN = 45,
  CROSSBOW = 46,
  JACKET = 47,
  COFFEECAN_HELMET = 48,
  GRENADE = 49,
  REACTIVE_SIGN = 50,
  BUCKET_HELMET = 51,
  BURLAP_SHOES = 52,
  CAP = 53,
  MINERS_HAT = 54,
  COLLARED_SHIRT = 55,
  BANDANA = 56,
  HIDE_HALTERNECK = 57,
  SATCHEL_EXPLOSIVES = 58,
  BEENIE = 59,
  SALVAGED_ICEPICK = 60,
  BOONIE = 61,
  BURLAP_HEADWRAP = 62,
  HIDE_PONCHO = 63,
  CONCRETE_BARRICADE = 64,
  DEER_SKULL_MASK = 65,
  HIDE_SKIRT = 66,
  SANDBAG_BARRICADE = 67,
  TANK_TOP = 68
}

export const RUST_COLORS: Record<RustItemCategory, string> = {
  [RustItemCategory.WEAPON]: 'eb4b4b',
  [RustItemCategory.CLOTHING]: 'a7ec2e', // TODO add green color
  [RustItemCategory.ARMOR]: 'a7ec2e', // TODO add green color
  [RustItemCategory.MISC]: '5e98d9',
  [RustItemCategory.RESOURCE]: 'eb4b4b'
};

export type InventoryItem = {
  id: number;
  item: Item;
  isBonus: boolean;
  priceUsd: number;
};

export type WsInventoryItem = InventoryItem & {
  item: { id: number }
};

export interface ItemOutOfStock {
  callback: () => void,
  unStockItems: Item[];
}

export namespace ApiResponse {
  export interface ItemsList {
    items: Item[];
    count: number;
    updatedAt: number;
  }

  export interface InventoryResponse {
    inventory: InventoryItem[];
  }

  export interface DetailedInventoryResponse extends InventoryResponse {
    page: number;
    total: number;
    lastPage: number;
    needPlayGames: number;
  }
  
  export interface WithdrawItem {
    inventoryItem: InventoryItem;
  }
}